import {
    BulkUpdateImportedAccountsBody,
    ImportedAccount,
} from "@common/schemas/domains/accounting/importedAccount.schema";
import { ImportedAccountGroup } from "@common/schemas/domains/accounting/importedAccountGroup.schema";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { orderedAccountClasses } from "@common/constants/domains/accounting/helpers";
import { accountsInImportedAccountGroupSubtree } from "@common/helpers/asIsTypeReviewInheritance";
import { Entity } from "@common/types/entity";
import { useAccounts } from "@hooks/useAccounts";
import { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { useBulkUpdateImportedAccounts } from "@api/accountingIntegration.api";
import { CommandsProvider } from "@components/commands/CommandsProvider";
import { SpreadsheetGrid } from "../spreadsheet/SpreadsheetGrid";
import {
    SpreadsheetColumnResizeProvider,
    SpreadsheetGridTemplate,
} from "@components/spreadsheet/SpreadsheetColumnResizeProvider";
import {
    appendSpreadsheetActionsColumn,
    SpreadsheetActionsColumnConfig,
} from "../spreadsheet/spreadsheetActionsColumn";
import { SpreadsheetData } from "../spreadsheet/types";
import { MapImportedAccountsHeader } from "./MapImportedAccountsHeader";
import { ClassificationSectionHeader } from "./ClassificationSectionHeader";
import { AccountMappingSpreadsheetRow, AccountRow } from "./types";
import { AccountMappingActionCell } from "./AccountMappingActionCell";
import { buildClassificationRows } from "./buildClassificationRows";
import { makeColumnsForGroup } from "./accountMappingColumns";
import { isAccountRow, isGroupRow } from "./rowTypeGuards";
import {
    getCreateNewAccountUpdate,
    getMapToAccountUpdate,
    getMarkAsFinancialUpdate,
    getUnmarkAsFinancialUpdate,
} from "./accountMappingBulkEligibility";
import {
    ACCOUNT_MAPPING_ACTIONS_COLUMN_WIDTH_PX,
    accountMappingGridTemplateColumns,
} from "./accountMappingGridTemplate";
import { isRowShowingMappingLoading } from "./accountMappingLoadingState";
import {
    resolveImportedAccountDisplayType,
    resolveMappedKickAccount,
} from "./resolveMappedKickAccount";
import styles from "./MapImportedAccountsSpreadsheet.module.scss";

interface Props {
    accounts: ImportedAccount[];
    accountGroups?: ImportedAccountGroup[];
    entity: Entity;
    workspaceId: string;
    entityId: number;
    providerHeader: React.ReactNode;
    isAccountsMappingRunning?: boolean;
    className?: string;
    isTypeReview?: boolean;
    providerLabel?: string;
}

export const MapImportedAccountsSpreadsheet: React.FC<Props> = ({
    accounts,
    accountGroups = [],
    entity,
    workspaceId,
    entityId,
    providerHeader,
    isAccountsMappingRunning,
    className,
    isTypeReview = false,
    providerLabel = "Imported",
}) => {
    const { accounts: kickAccounts } = useAccounts({
        entityId: entity.id,
        includeDisabled: true,
    });
    const includeActions = !isTypeReview;
    const gridTemplateColumns = accountMappingGridTemplateColumns({
        isTypeReview,
        includeActions,
    });

    const [collapsedClassifications, setCollapsedClassifications] = useState<
        Set<AccountClass>
    >(new Set());

    const toggleClassification = useCallback((classification: AccountClass) => {
        setCollapsedClassifications((prev) => {
            const next = new Set(prev);
            if (next.has(classification)) {
                next.delete(classification);
            } else {
                next.add(classification);
            }
            return next;
        });
    }, []);

    const { mutate: bulkUpdate } = useBulkUpdateImportedAccounts(
        workspaceId,
        entityId,
    );

    const kickAccountsById = useMemo(
        () => Object.fromEntries(kickAccounts.map((a) => [a.id, a])),
        [kickAccounts],
    );

    const accountRows: AccountRow[] = useMemo(
        () =>
            accounts.map((account) => {
                const mappedKickAccount = resolveMappedKickAccount({
                    account,
                    kickAccountsById,
                });
                const { accountType, accountSubtype } =
                    resolveImportedAccountDisplayType({
                        isTypeReview,
                        kickType: account.kickType,
                        kickSubtype: account.kickSubtype,
                        mappedKickAccount,
                    });

                return {
                    _id: account.id,
                    _depth: 0,
                    _isGroup: false,
                    name: account.name,
                    providerType: account.providerType,
                    kickAccountId: mappedKickAccount
                        ? mappedKickAccount.id
                        : null,
                    kickType: account.kickType,
                    accountClass: account.classification,
                    accountType,
                    accountSubtype,
                    financialType: account.financialType,
                    accountConnectionTaskId:
                        account.accountConnectionTaskId ?? null,
                    detectedInstitutionName:
                        account.detectedInstitutionName ?? null,
                    createNewAccount: account.createNewAccount,
                    importedAccountGroupId: account.importedAccountGroupId,
                    sortOrder: account.sortOrder,
                };
            }),
        [accounts, kickAccountsById, isTypeReview],
    );

    const classificationGroups = useMemo(
        () =>
            orderedAccountClasses
                .map((classification) => {
                    const result = buildClassificationRows(
                        classification,
                        accountGroups,
                        accountRows,
                    );
                    return {
                        classification,
                        rows: result.rows,
                        accountCount: result.accountCount,
                    };
                })
                .filter(({ rows }) => rows.length > 0),
        [accountRows, accountGroups],
    );

    const shimmerClassName = useCallback(
        (row: Record<string, unknown>) => {
            if (isGroupRow(row)) {
                return "";
            }
            if (isAccountRow(row)) {
                return isRowShowingMappingLoading({
                    row,
                    isAccountsMappingRunning: !!isAccountsMappingRunning,
                })
                    ? "animation-shimmer"
                    : "";
            }
            return "";
        },
        [isAccountsMappingRunning],
    );

    const handleCreateNewAccount = useCallback(
        (row: AccountRow) => {
            bulkUpdate({
                updates: [
                    getCreateNewAccountUpdate({
                        id: row._id,
                        kickType: row.kickType,
                    }),
                ],
            });
        },
        [bulkUpdate],
    );

    const handleUndoCreateNewAccount = useCallback(
        (importedAccountId: string) => {
            bulkUpdate({
                updates: [
                    {
                        importedAccountId,
                        createNewAccount: false,
                    },
                ],
            });
        },
        [bulkUpdate],
    );

    const handleUndoMapping = useCallback(
        (importedAccountId: string) => {
            bulkUpdate({
                updates: [
                    {
                        importedAccountId,
                        accountId: null,
                        createNewAccount: true,
                    },
                ],
            });
        },
        [bulkUpdate],
    );

    const handleMarkAsFinancial = useCallback(
        (row: AccountRow) => {
            bulkUpdate({
                updates: [
                    getMarkAsFinancialUpdate({
                        id: row._id,
                        accountClass: row.accountClass,
                    }),
                ],
            });
        },
        [bulkUpdate],
    );

    const handleUnmarkAsFinancial = useCallback(
        (importedAccountId: string) => {
            bulkUpdate({
                updates: [getUnmarkAsFinancialUpdate(importedAccountId)],
            });
        },
        [bulkUpdate],
    );

    const actionsColumnConfig: SpreadsheetActionsColumnConfig = useMemo(
        () => ({
            label: "Actions",
            width: ACCOUNT_MAPPING_ACTIONS_COLUMN_WIDTH_PX,
            cellFn: ({ row }) => {
                if (isGroupRow(row)) {
                    return null;
                }
                if (!isAccountRow(row)) {
                    return null;
                }
                return (
                    <AccountMappingActionCell
                        row={row}
                        isAccountsMappingActive={
                            isTypeReview ||
                            isRowShowingMappingLoading({
                                row,
                                isAccountsMappingRunning:
                                    !!isAccountsMappingRunning,
                            })
                        }
                        onCreateNewAccount={handleCreateNewAccount}
                        onUndoCreateNewAccount={handleUndoCreateNewAccount}
                        onUndoMapping={handleUndoMapping}
                        onMarkAsFinancial={handleMarkAsFinancial}
                        onUnmarkAsFinancial={handleUnmarkAsFinancial}
                    />
                );
            },
        }),
        [
            handleCreateNewAccount,
            handleUndoCreateNewAccount,
            handleUndoMapping,
            handleMarkAsFinancial,
            handleUnmarkAsFinancial,
            isAccountsMappingRunning,
            isTypeReview,
        ],
    );

    const makeHandleChange = useCallback(
        (groupRows: AccountMappingSpreadsheetRow[]) =>
            (newData: SpreadsheetData) => {
                const updates: BulkUpdateImportedAccountsBody["updates"] = [];

                newData.forEach((newRow, i) => {
                    const oldRow = groupRows[i];
                    if (!oldRow || !newRow) {
                        return;
                    }

                    const typeChanged =
                        oldRow.accountType !== newRow.accountType;
                    if (oldRow._isGroup) {
                        if (!isTypeReview || !typeChanged) {
                            return;
                        }
                        const subtreeAccounts =
                            accountsInImportedAccountGroupSubtree({
                                rootGroupId: oldRow._id,
                                groups: accountGroups,
                                accounts: groupRows.filter(isAccountRow),
                            });
                        for (const account of subtreeAccounts) {
                            updates.push({
                                importedAccountId: account._id,
                                kickType: newRow.accountType,
                            });
                        }
                        return;
                    }

                    const kickAccountChanged =
                        oldRow.kickAccountId !== newRow.kickAccountId;

                    if (!kickAccountChanged && !typeChanged) {
                        return;
                    }

                    if (kickAccountChanged) {
                        updates.push(
                            getMapToAccountUpdate({
                                id: oldRow._id,
                                accountId: newRow.kickAccountId ?? null,
                            }),
                        );
                        return;
                    }

                    updates.push({
                        importedAccountId: oldRow._id,
                        kickType: newRow.accountType,
                    });
                });

                if (updates.length > 0) {
                    bulkUpdate({ updates });
                }
            },
        [bulkUpdate, isTypeReview, accountGroups],
    );

    const memoizedGroups = useMemo(
        () =>
            classificationGroups.map(
                ({ classification, rows, accountCount }) => {
                    const columns = makeColumnsForGroup({
                        groupRows: rows,
                        kickAccounts,
                        shimmerClassName,
                        providerLabel,
                        hideKickAccountColumn: isTypeReview,
                        isTypeReview,
                    });

                    return {
                        classification,
                        rows,
                        accountCount,
                        columns,
                        gridColumns: includeActions
                            ? appendSpreadsheetActionsColumn(
                                  columns,
                                  actionsColumnConfig,
                              )
                            : columns,
                        handleChange: makeHandleChange(rows),
                    };
                },
            ),
        [
            classificationGroups,
            kickAccounts,
            shimmerClassName,
            makeHandleChange,
            isTypeReview,
            providerLabel,
            includeActions,
            actionsColumnConfig,
        ],
    );

    return (
        <CommandsProvider useDefaultKeyboardCommand={false}>
            <SpreadsheetColumnResizeProvider
                columnLayoutKey={gridTemplateColumns}
            >
                <div className={className}>
                    <SpreadsheetGridTemplate
                        defaultGridTemplateColumns={gridTemplateColumns}
                        className={classNames(styles.standaloneHeader, {
                            [styles.standaloneHeaderWithTypeReview]:
                                isTypeReview,
                        })}
                    >
                        <MapImportedAccountsHeader
                            providerHeader={providerHeader}
                        />
                    </SpreadsheetGridTemplate>

                    {memoizedGroups.map(
                        ({
                            classification,
                            rows,
                            accountCount,
                            gridColumns,
                            handleChange,
                        }) => {
                            const isExpanded =
                                !collapsedClassifications.has(classification);
                            return (
                                <div key={classification}>
                                    <ClassificationSectionHeader
                                        classification={classification}
                                        count={accountCount}
                                        isExpanded={isExpanded}
                                        onToggle={() =>
                                            toggleClassification(classification)
                                        }
                                    />
                                    {isExpanded && (
                                        <SpreadsheetGrid
                                            rowClassName={styles.row}
                                            noOuterBorder
                                            data={rows}
                                            columns={gridColumns}
                                            onChange={handleChange}
                                            gridTemplateColumns={
                                                gridTemplateColumns
                                            }
                                            lightNotEditableCells
                                        />
                                    )}
                                </div>
                            );
                        },
                    )}
                </div>
            </SpreadsheetColumnResizeProvider>
        </CommandsProvider>
    );
};
